/**
 * Admin V2 — Marketplace Command Center shell.
 *
 * Replaces the legacy `AdminTopNav` with a desktop-first sidebar covering
 * the six operations pillars. Each section can contain multiple panels —
 * the sidebar surfaces the primary page; secondary tabs live inside each
 * page's header.
 *
 * Branding: Navy #081B4B · Orange #F97316 · White.
 * Tablet ≥ 768px is supported; phones intentionally fall back (admin is a
 * desktop ops tool, the Rider/Driver apps are the mobile products).
 */
import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Map,
  Radio,
  CircleDollarSign,
  Users,
  Settings,
  LogOut,
  ExternalLink,
} from "lucide-react";
import { useAuth } from "@/shared/context/AuthContext";

const NAV = [
  { to: "/admin/v2/command",     label: "Command Center", Icon: LayoutDashboard },
  { to: "/admin/v2/marketplace", label: "Marketplace",    Icon: Map },
  { to: "/admin/v2/operations",  label: "Operations",     Icon: Radio },
  { to: "/admin/v2/finance",     label: "Finance",        Icon: CircleDollarSign },
  { to: "/admin/v2/drivers",     label: "Drivers",        Icon: Users },
  { to: "/admin/v2/settings",    label: "Settings",       Icon: Settings },
];

export default function AdminShell({ children, title, subtitle, headerActions }) {
  const { logout, user } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-[100dvh] flex bg-[#F4F4F6] text-[#0F172A]" data-testid="admin-shell">
      {/* Sidebar */}
      <aside
        className="hidden md:flex w-[232px] shrink-0 flex-col bg-[#081B4B] text-white sticky top-0 h-[100dvh] border-r border-black/10"
        data-testid="admin-sidebar"
      >
        <div className="px-5 pt-6 pb-5 border-b border-white/10">
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">Link</div>
          <div className="font-serif text-[22px] leading-none mt-1">Command<span className="text-[#F97316]">.</span></div>
          <div className="text-[10px] text-white/55 mt-1.5">Marketplace operations</div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1">
          {NAV.map(({ to, label, Icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] transition-colors ${
                  isActive
                    ? "bg-[#F97316] text-white shadow-sm"
                    : "text-white/70 hover:text-white hover:bg-white/5"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Icon size={16} strokeWidth={1.8} />
              {label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 py-4 border-t border-white/10 space-y-1">
          <button
            onClick={() => nav("/")}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-white/60 hover:text-white hover:bg-white/5"
            data-testid="admin-nav-exit"
          >
            <ExternalLink size={14} /> Rider app
          </button>
          <div className="px-3 py-1 text-[10px] text-white/40 tracking-wider truncate">
            {user?.email}
          </div>
          <button
            onClick={onLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-[12px] text-white/60 hover:text-[#F97316] hover:bg-white/5"
            data-testid="admin-nav-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-black/[0.06] px-6 lg:px-8 py-5" data-testid="admin-header">
          <div className="flex items-end justify-between gap-4 flex-wrap">
            <div>
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#F97316] font-semibold">Link Operations</div>
              <h1 className="font-serif text-[28px] lg:text-[34px] leading-none mt-1 text-[#081B4B]">{title}</h1>
              {subtitle && <p className="text-[13px] text-[#475569] mt-2 max-w-2xl">{subtitle}</p>}
            </div>
            {headerActions && <div className="flex items-center gap-2 flex-wrap">{headerActions}</div>}
          </div>
        </header>
        <div className="flex-1 px-6 lg:px-8 py-6 lg:py-8" data-testid="admin-content">
          {children}
        </div>
        {/* Mobile fallback notice */}
        <div className="md:hidden fixed inset-x-0 bottom-0 bg-[#081B4B] text-white text-[12px] px-4 py-3 text-center">
          Best viewed on tablet or desktop · 768px+
        </div>
      </main>
    </div>
  );
}
